// RAPIFAST :: transporte temporal de datos del registro BUSINESS y sincronización
// diferida con la RPC real upsert_my_business_verification (migración 0024).
// REGLAS (auditoría SEC-001..SEC-004 aplicadas):
//  - El borrador local NO es un registro persistente ni sustituye a Supabase:
//    es solo transporte hasta que exista sesión y la RPC esté disponible (0024).
//  - Datos mínimos: municipio, dirección y marca de aceptación. NUNCA contraseñas,
//    tokens ni secretos. El tipo de negocio NO se guarda (solo vive en el formulario).
//  - Ciclo de vida: TTL 7 días (suficiente para el flujo de confirmación de correo);
//    se elimina al sincronizar con éxito, al expirar, al cerrar sesión, al cambiar
//    de cuenta en el mismo navegador, o si el usuario lo descarta desde el banner.
//  - Mientras 0024 no esté aplicada, la RPC no existe (PGRST202): se detecta de
//    forma específica y el borrador se conserva en silencio. CUALQUIER otro error
//    (403/42501, 500, red, desconocido) se muestra como error real con reintento.
import { upsertMyBusinessVerification } from "@/lib/verificationApi";

// Marca provisional del CLIENTE (reloj del navegador). No es hora verificada por el
// servidor: la marca definitiva deberá registrarla el servidor cuando 0024 y el
// flujo persistente estén disponibles (agreement_accepted_at sella la sincronización).
export const LEGAL_VERSION_LABEL =
  "borrador-piloto-v1.0 (documentos internos en revisión); aceptación declarada en formulario (marca provisional de cliente, pendiente de sello de servidor)";

// Conservación mínima necesaria: 7 días (cubre confirmación de correo + primer ingreso).
export const DRAFT_TTL_MS = 7 * 24 * 3600 * 1000;

const PREFIX = "rapifast_biz_pending_";
export const draftKey = (email) => `${PREFIX}${String(email || "").trim().toLowerCase()}`;

export function saveBusinessDraft(email, data) {
  try {
    localStorage.setItem(draftKey(email), JSON.stringify({
      municipality: data.municipality,
      pickup_address: data.pickup_address,
      legal_accepted_at: data.legal_accepted_at,
      created_at: new Date().toISOString(),
    }));
  } catch { /* almacenamiento local no disponible: no bloqueante */ }
}

export function readBusinessDraft(email) {
  try {
    const raw = localStorage.getItem(draftKey(email));
    if (!raw) return null;
    const d = JSON.parse(raw);
    const created = Date.parse(d.created_at || "");
    if (!created || Date.now() - created > DRAFT_TTL_MS) {
      localStorage.removeItem(draftKey(email)); // expirado o inválido: se descarta
      return null;
    }
    return d;
  } catch {
    return null;
  }
}

export function clearBusinessDraft(email) {
  try { localStorage.removeItem(draftKey(email)); } catch { /* no bloqueante */ }
}

// Elimina TODOS los borradores de negocio del dispositivo (cierre de sesión).
export function clearAllBusinessDrafts() {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch { /* no bloqueante */ }
}

// Cambio de cuenta: conserva únicamente el borrador del correo con sesión activa.
export function pruneOtherBusinessDrafts(currentEmail) {
  try {
    const keep = draftKey(currentEmail);
    Object.keys(localStorage)
      .filter((k) => k.startsWith(PREFIX) && k !== keep)
      .forEach((k) => localStorage.removeItem(k));
  } catch { /* no bloqueante */ }
}

// Detección ESPECÍFICA de "la RPC aún no existe" (0024 pendiente): solo el código
// PostgREST PGRST202 o el mensaje de función ausente que mencione ESTA función.
// Nada más se silencia: 403/42501, 500, red o errores desconocidos son errores reales.
function isMissingRpc(err) {
  const code = String(err?.code || "");
  const msg = String(err?.message || err || "");
  // PGRST202 solo silencia si el mensaje corresponde a ESTA RPC (defensa en profundidad).
  if (code === "PGRST202") return !msg || /upsert_my_business_verification/i.test(msg);
  return /could not find the function/i.test(msg) && /upsert_my_business_verification/i.test(msg);
}

// Sincroniza el borrador con business_verifications vía la RPC de 0024.
// Devuelve: "none" (sin borrador), "synced", "pending_migration" (RPC aún no
// existe; conservar borrador en silencio), "error" (real, reintentable).
export async function syncBusinessVerification({ email, fullName, phone, businessName }) {
  const draft = readBusinessDraft(email);
  if (!draft) return { status: "none" };
  try {
    await upsertMyBusinessVerification({
      legalName: businessName || null,
      // Dirección + municipio en la única columna de dirección disponible (sin 0029).
      address: `${draft.pickup_address}, ${draft.municipality}`,
      contactName: fullName || null,
      contactPhone: phone || null,
      // La hora REAL del checkbox viaja como marca provisional de cliente en el texto.
      agreementVersion: `${LEGAL_VERSION_LABEL}: ${draft.legal_accepted_at}`,
    });
    clearBusinessDraft(email);
    return { status: "synced" };
  } catch (err) {
    if (isMissingRpc(err)) return { status: "pending_migration" };
    return { status: "error", message: String(err?.message || err || "") };
  }
}
